import React, { createContext, useContext, useState, useCallback } from 'react';

export interface UserInfo {
  username: string;
  password: string;
  status: string;
  auth: number;
  exp_date: string;
  is_trial: string;
  active_cons: string;
  created_at: string;
  max_connections: string;
  allowed_output_formats: string[];
}

export interface ServerInfo {
  url: string;
  port: string;
  https_port?: string;
  server_protocol: string;
  timezone?: string;
  /** Complete backend-provided Xtream endpoint, including scheme and path. */
  baseUrl?: string;
}

export function getXtreamServerUrl(server: ServerInfo): string {
  if (server.baseUrl?.trim()) return server.baseUrl.trim().replace(/\/+$/, '');
  if (/^https?:\/\//i.test(server.url)) return server.url.trim().replace(/\/+$/, '');
  const protocol = server.server_protocol || 'http';
  const defaultPort = protocol === 'https' ? '443' : '80';
  const port = server.port && server.port !== defaultPort ? `:${server.port}` : '';
  return `${protocol}://${server.url}${port}`.replace(/\/+$/, '');
}

export interface AuthData {
  user: UserInfo;
  server: ServerInfo;
  accessCode: string;
  profileDisplayName?: string;
  profileUsesCode?: boolean;
  /** Stable backend profile ID used by Watch Sync V2 and local cache scope. */
  profileId?: string;
}

interface AuthContextType {
  authData: AuthData | null;
  isAuthenticated: boolean;
  login: (authData: AuthData) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const STORAGE_KEY = 'ott_player_auth_data';
export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [authData, setAuthData] = useState<AuthData | null>(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return saved ? JSON.parse(saved) : null;
    } catch {
      return null;
    }
  });

  const login = useCallback((data: AuthData) => {
    setAuthData(data);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch {
      // storage full or unavailable
    }
  }, []);

  const logout = useCallback(() => {
    setAuthData(null);
    localStorage.removeItem(STORAGE_KEY);
  }, []);

  return (
    <AuthContext.Provider
      value={{ authData, isAuthenticated: !!authData, login, logout }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
